import React from 'react';

import { client } from '~/api/client';
import {
  type CaraWorkspaceArtifactEmailInput,
  type CaraWorkspaceEmailAmsLogInput,
  type EmailAmsMatchFieldsFragment,
  EmailAmsMatchOutcome,
  ResolveEmailAmsAccountsDocument,
  type ResolveEmailAmsAccountsQuery,
  type ResolveEmailAmsAccountsQueryVariables
} from '~/api/graphql';
import {
  type ChosenClient,
  refInputOf,
  filingTargetInputOf
} from '~/components/ams-email-log/client-list';
import { useDebouncedValue } from '~/hooks/use-list-controls';

export type EmailAmsMatch = EmailAmsMatchFieldsFragment;
export type EmailAmsChoice = NonNullable<EmailAmsMatch['choice']>;

/**
 * The client a send to the To on screen would log to: the agent's choice, else a single
 * match. Null when it isn't logged, or nobody has chosen among several.
 */
export const amsClientLoggedTo = (
  match: EmailAmsMatch | null,
  choice: EmailAmsChoice | null
): ChosenClient | null => {
  if (choice?.target) {
    return {
      target: refInputOf(choice.target),
      displayName: choice.displayName ?? '',
      filingTarget: choice.filingTarget ? filingTargetInputOf(choice.filingTarget) : undefined
    };
  }
  const single = match?.outcome === EmailAmsMatchOutcome.OneMatch ? match.candidates[0] : null;
  return !choice && single
    ? { target: refInputOf(single.account), displayName: single.displayName }
    : null;
};

/** Long enough that adding a second recipient right after the first asks once. */
const TO_SETTLE_MS = 400;

/** One key per To list, so an equal list rebuilt by a render doesn't match again. */
const toKeyOf = (toAddresses: string[]) => toAddresses.join('\n');

type MatchAnswer = { toKey: string; match: EmailAmsMatch | null };

/**
 * Matches the email's To addresses to clients in the AMS that logs the agency's sent
 * emails whenever they settle on a new list, and saves the agent's choice for them.
 * While a new list is matched, `match` stays the previous one's, so the line can name
 * the AMS it is checking.
 */
export const useEmailAmsMatch = (args: {
  enabled: boolean;
  workspaceId: string;
  artifactId: string;
  toAddresses: string[];
  draftKey?: string;
  save: (email: CaraWorkspaceArtifactEmailInput) => Promise<boolean>;
}) => {
  const { enabled, workspaceId, artifactId, draftKey, save } = args;
  const toKey = toKeyOf(args.toAddresses);
  const settledToKey = useDebouncedValue(toKey, TO_SETTLE_MS);
  const hasRecipients = settledToKey !== '';

  const [answer, setAnswer] = React.useState<MatchAnswer | null>(null);
  /** The To list whose latest match failed. */
  const [failedToKey, setFailedToKey] = React.useState<string | null>(null);
  const [attempt, setAttempt] = React.useState(0);
  /** A choice saved from here, shown until the match that includes it comes back. */
  const [savedChoice, setSavedChoice] = React.useState<{
    toKey: string;
    choice: EmailAmsChoice | null;
  } | null>(null);
  /**
   * Counts the saves started and those still in flight. Only a match asked for after
   * the latest save returned can carry its choice, so only that one replaces savedChoice.
   */
  const saveGeneration = React.useRef(0);
  const pendingSaves = React.useRef(0);

  React.useEffect(() => {
    if (!enabled || !hasRecipients) return;
    let current = true;
    const generationAsked = saveGeneration.current;
    const isAskedAfterSaves = pendingSaves.current === 0;
    setFailedToKey(null);
    client
      .query<ResolveEmailAmsAccountsQuery, ResolveEmailAmsAccountsQueryVariables>({
        query: ResolveEmailAmsAccountsDocument,
        variables: { workspaceId, artifactId, toAddresses: settledToKey.split('\n') },
        fetchPolicy: 'no-cache',
        // A match asked again after a save must reach the server, not join the
        // identical request that started before it.
        context: { queryDeduplication: false }
      })
      .then((result) => {
        if (!current) return;
        if (result.errors?.length) {
          setFailedToKey(settledToKey);
          return;
        }
        setAnswer({ toKey: settledToKey, match: result.data?.resolveEmailAmsAccounts ?? null });
        const carriesLatestSave = isAskedAfterSaves && generationAsked === saveGeneration.current;
        if (carriesLatestSave) setSavedChoice(null);
      })
      .catch((error: unknown) => {
        // eslint-disable-next-line no-console
        console.error(error);
        if (current) setFailedToKey(settledToKey);
      });
    return () => {
      current = false;
    };
  }, [enabled, hasRecipients, workspaceId, artifactId, settledToKey, draftKey, attempt]);

  const retry = React.useCallback(() => setAttempt((count) => count + 1), []);

  /** Saves the choice for the To addresses on screen; null clears it. */
  const saveChoice = React.useCallback(
    async (amsLog: CaraWorkspaceEmailAmsLogInput | null, shown: EmailAmsChoice | null) => {
      const generation = ++saveGeneration.current;
      pendingSaves.current += 1;
      setSavedChoice({ toKey, choice: shown });
      const isSaved = await save({ amsLog }).finally(() => {
        pendingSaves.current -= 1;
      });
      const isLatestSave = generation === saveGeneration.current;
      if (isSaved) {
        retry();
      } else if (isLatestSave) {
        setSavedChoice(null);
      }
      return isSaved;
    },
    [save, retry, toKey]
  );

  const isMatchShown = enabled && toKey !== '';
  const isCurrent = answer?.toKey === toKey;
  const hasFailed = isMatchShown && failedToKey === toKey;
  const choice =
    savedChoice?.toKey === toKey ? savedChoice.choice : isCurrent ? answer.match?.choice : null;
  return {
    /**
     * The latest match, for the To addresses on screen only when `isMatching` is false.
     * Null while matching is off or To is empty, so a sent email or an emptied To shows
     * no stale answer.
     */
    match: isMatchShown ? (answer?.match ?? null) : null,
    choice: isMatchShown ? (choice ?? null) : null,
    isMatching: isMatchShown && !hasFailed && !isCurrent,
    /**
     * Whether matching the To on screen failed. Before any answer the match is null, so
     * the line shows no error until a first answer has named the AMS.
     */
    hasFailed,
    hasRecipients: toKey !== '',
    retry,
    saveChoice
  };
};
